import { Adornment, AdornmentType, GutterAdornment } from "Presentation/Scripts/TFS/TFS.Adornment.Common";
import { IGutterAdornmentCollection, IndicatorType } from "VC/Pullrequest/Common/GutterAdornments";
import { IPullRequestAdornmentService, IPullRequestFileContext } from "VC/Pullrequest/Common/PullRequestAdornmentService";
import * as Actions from "VersionControl/Scripts/Actions/PullRequestReview/ActionsHub";
import { DiffViewerExtensionSource } from "VersionControl/Scripts/Sources/DiffViewerExtensionSource";
import { IPullRequestAdornmentContext } from "VersionControl/Scripts/IPullRequestAdornmentContext";
import { DiffViewerExtensionStore, DiffViewerExtensionsStatus } from "VersionControl/Scripts/Stores/PullRequestReview/DiffViewerExtensionStore";

/**
 * Pull Request Gutter Adornment action creator
 */

export class DiffViewerExtensionActionCreator  {
    constructor(private actionsHub: Actions.ActionsHub, private source: DiffViewerExtensionSource, private store: DiffViewerExtensionStore) {
    }

    public fetchAndInitializeExtensions(pullRequestAdornmentContext: IPullRequestAdornmentContext) {
        if(this.store.extensionsStatus == DiffViewerExtensionsStatus.NotLoaded) {
            this.store.extensionsStatus = DiffViewerExtensionsStatus.Loading;

            this.source.fetchExtensionContributions().then((services: IPullRequestAdornmentService[]) => {
                // Don't disturb the component state if services aren't there
                if (services && services.length) {
                    services.forEach((service: IPullRequestAdornmentService) => {
                        service.initializeExtension(pullRequestAdornmentContext.pullRequestContext);
                    });

                    this.actionsHub.diffViewerExtensionsLoaded.invoke(services);
                    this.loadingComplete();

                    if (!pullRequestAdornmentContext.isDirectory) {
                        this.fetchAdornmentsForNewContext(pullRequestAdornmentContext.pullRequestFileContext);
                    }
                }

            }, this.loadingComplete);
        }
    }

    public fetchAdornmentsForNewContext(context: IPullRequestFileContext) {
        // Fetch again only if the pull request file context has changed
        if (this.store.getCurrentPullRequestFileContext() !== context) {
            const selectedExtension = this.store.getSelectedExtension();
            if (selectedExtension && (context.mPath || context.oPath)) {
                this.store.extensionsStatus = DiffViewerExtensionsStatus.Loading;
                selectedExtension.getGutterAdornments(context).then((adornmentCollection: IGutterAdornmentCollection) => {
                    this.actionsHub.diffViewerExtensionGutterAdornmentsLoaded.invoke({
                        context: context,
                        adornments: this.getAdornmentsFromCollection(adornmentCollection)
                    });
                    
                    this.loadingComplete();
                }, this.loadingComplete);
            }
        }
    }

    // Convert PR adornment object to monaco adornment object
    private getAdornmentsFromCollection(adornmentCollection: IGutterAdornmentCollection): Adornment[] {
        const adornments = [];
        let id = 0;

        adornmentCollection.originalFilegutterAdornments.forEach((gutterAdornment) => {
            adornments.push(<GutterAdornment>{
                id: id++,
                startLine: gutterAdornment.LineNumberStart,
                endLine: gutterAdornment.LineNumberStart + gutterAdornment.LinesCount,
                isOriginalSide: true,
                adornmentType: AdornmentType.GUTTER,
                className: this.getAdornmentClassName(gutterAdornment.IndicatorType),
                showLineNumber: true,
                helpText: gutterAdornment.HelpText
            });
        });
        adornmentCollection.modifiedFilegutterAdornments.forEach((gutterAdornment) => {
            adornments.push(<GutterAdornment>{
                id: id++,
                startLine: gutterAdornment.LineNumberStart,
                endLine: gutterAdornment.LineNumberStart + gutterAdornment.LinesCount,
                isOriginalSide: false,
                adornmentType: AdornmentType.GUTTER,
                className: this.getAdornmentClassName(gutterAdornment.IndicatorType),
                showLineNumber: true,
                helpText: gutterAdornment.HelpText
            });
        });

        return adornments;
    }

    private getAdornmentClassName(indicator: IndicatorType) {
        switch (indicator) {
            case IndicatorType.Error:
                return "gutter-adornment failed";
            case IndicatorType.Succeeded:
                return "gutter-adornment succeeded";
            case IndicatorType.Warning:
                return "gutter-adornment warning";
        }
    }

    private loadingComplete = () => {
        this.store.extensionsStatus = DiffViewerExtensionsStatus.Loaded;
    }
}
